// src/styles/liquidTheme.js - Modern Liquid UI Theme

const liquidTheme = {
  // Light mode specific colors
  light: {
    background: {
      main: '#f5f7fa',
      gradient: 'linear-gradient(-45deg, #ffecd2, #fcb69f, #a8edea, #fed6e3, #d299c2, #fef9d7)',
    },
    glass: {
      background: 'rgba(255, 255, 255, 0.7)',
      backgroundHover: 'rgba(255, 255, 255, 0.85)',
      border: 'rgba(255, 255, 255, 0.8)',
      shadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.9)',
      secondary: 'rgba(0, 0, 0, 0.65)',
      muted: 'rgba(0, 0, 0, 0.45)',
    },
  },
  
  // Dark mode specific colors
  dark: {
    background: {
      main: '#0f0f23',
      gradient: 'linear-gradient(-45deg, #667eea, #764ba2, #f093fb, #4facfe, #43e97b, #fa709a)',
    },
    glass: {
      background: 'rgba(255, 255, 255, 0.05)',
      backgroundHover: 'rgba(255, 255, 255, 0.08)',
      border: 'rgba(255, 255, 255, 0.1)',
      shadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.95)',
      secondary: 'rgba(255, 255, 255, 0.7)',
      muted: 'rgba(255, 255, 255, 0.5)',
    },
  },
  
  colors: {
    // Primary gradients for liquid effect
    primary: {
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      light: '#8b9dff',
      main: '#667eea',
      dark: '#5568d3',
      glow: 'rgba(102, 126, 234, 0.4)',
    },
    secondary: {
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      light: '#ff94aa',
      main: '#f5576c',
      dark: '#e04858',
      glow: 'rgba(245, 87, 108, 0.4)',
    },
    success: {
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      light: '#6bddff',
      main: '#4facfe',
      dark: '#3a8bd4',
      glow: 'rgba(79, 172, 254, 0.4)',
    },
    warning: {
      gradient: 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)',
      light: '#c084fc',
      main: '#a855f7',
      dark: '#9333ea',
      glow: 'rgba(168, 85, 247, 0.4)',
    },
    danger: {
      gradient: 'linear-gradient(135deg, #f857a6 0%, #ff5858 100%)',
      light: '#ff6b8b',
      main: '#f857a6',
      dark: '#e04891',
      glow: 'rgba(248, 87, 166, 0.4)',
    },
    info: {
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      light: '#c4f2ef',
      main: '#a8edea',
      dark: '#8dd4d1',
      glow: 'rgba(168, 237, 234, 0.4)',
    },
    
    // Glass morphism backgrounds
    glass: {
      white: 'rgba(255, 255, 255, 0.1)',
      whiteHover: 'rgba(255, 255, 255, 0.15)',
      dark: 'rgba(0, 0, 0, 0.1)',
      darkHover: 'rgba(0, 0, 0, 0.15)',
      frosted: 'rgba(255, 255, 255, 0.05)',
      blur: 'blur(20px)',
      blurStrong: 'blur(40px)',
    },
    
    // Animated gradients for background
    animatedGradients: {
      cosmic: `linear-gradient(-45deg, 
        #667eea, #764ba2, #f093fb, #4facfe,
        #43e97b, #fa709a, #fee140, #30cfd0
      )`,
      ocean: `linear-gradient(-45deg,
        #2E3192, #1BFFFF, #00F2FE, #4FACFE,
        #667EEA, #43E97B, #38F9D7, #667eea
      )`,
      sunset: `linear-gradient(-45deg,
        #FA709A, #FEE140, #30CFD0, #667EEA,
        #F093FB, #F5576C, #4FACFE, #00F2FE
      )`,
      aurora: `linear-gradient(-45deg,
        #667eea, #764ba2, #f093fb, #f5576c,
        #43e97b, #38f9d7, #4facfe, #00f2fe
      )`,
    },
    
    // Status colors with gradients
    status: {
      available: {
        bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
        text: '#0a5d4d',
        glow: 'rgba(168, 237, 234, 0.5)',
      },
      occupied: {
        bg: 'linear-gradient(135deg, #f857a6 0%, #ff5858 100%)',
        text: '#fff',
        glow: 'rgba(248, 87, 166, 0.5)',
      },
      reserved: {
        bg: 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)',
        text: '#fff',
        glow: 'rgba(168, 85, 247, 0.5)',
      },
      cleaning: {
        bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        text: '#fff',
        glow: 'rgba(102, 126, 234, 0.5)',
      },
    },
    
    // Text colors
    text: {
      primary: 'rgba(255, 255, 255, 0.95)',
      secondary: 'rgba(255, 255, 255, 0.7)',
      muted: 'rgba(255, 255, 255, 0.5)',
      dark: 'rgba(0, 0, 0, 0.85)',
      darkSecondary: 'rgba(0, 0, 0, 0.65)',
    },
    
    // Background colors
    background: {
      main: '#0f0f23',
      card: 'rgba(255, 255, 255, 0.05)',
      cardHover: 'rgba(255, 255, 255, 0.08)',
      overlay: 'rgba(0, 0, 0, 0.7)',
    },
  },
  
  // Glass morphism effects
  glassEffects: {
    card: {
      background: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    },
    cardHover: {
      background: 'rgba(255, 255, 255, 0.08)',
      boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15)',
    },
    button: {
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
    },
    input: {
      background: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(15px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    },
  },
  
  // Shadows with glow effects
  shadows: {
    glow: {
      primary: '0 0 20px rgba(102, 126, 234, 0.4), 0 0 40px rgba(102, 126, 234, 0.2)',
      secondary: '0 0 20px rgba(245, 87, 108, 0.4), 0 0 40px rgba(245, 87, 108, 0.2)',
      success: '0 0 20px rgba(79, 172, 254, 0.4), 0 0 40px rgba(79, 172, 254, 0.2)',
      warning: '0 0 20px rgba(168, 85, 247, 0.4), 0 0 40px rgba(168, 85, 247, 0.2)',
    },
    soft: '0 4px 20px rgba(0, 0, 0, 0.1)',
    medium: '0 8px 32px rgba(0, 0, 0, 0.15)',
    strong: '0 16px 48px rgba(0, 0, 0, 0.2)',
    inner: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  
  // Border radius
  borderRadius: {
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    full: '9999px',
    blob: '30% 70% 70% 30% / 30% 30% 70% 70%', // organic shapes
  },
  
  // Animations
  animations: {
    // Floating animation
    float: `
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
      }
    `,
    // Pulse glow animation
    pulseGlow: `
      @keyframes pulseGlow {
        0%, 100% { 
          box-shadow: 0 0 20px rgba(102, 126, 234, 0.4), 0 0 40px rgba(102, 126, 234, 0.2);
          filter: drop-shadow(0 0 15px rgba(102, 126, 234, 0.5));
        }
        50% { 
          box-shadow: 0 0 30px rgba(102, 126, 234, 0.6), 0 0 60px rgba(102, 126, 234, 0.3);
          filter: drop-shadow(0 0 25px rgba(102, 126, 234, 0.7));
        }
      }
    `,
    // Gradient shift animation
    gradientShift: `
      @keyframes gradientShift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
    `,
    // Shimmer animation
    shimmer: `
      @keyframes shimmer {
        0% { background-position: -1000px 0; }
        100% { background-position: 1000px 0; }
      }
    `,
    // Ripple animation
    ripple: `
      @keyframes ripple {
        0% {
          transform: scale(0);
          opacity: 1;
        }
        100% {
          transform: scale(4);
          opacity: 0;
        }
      }
    `,
    // Slide in from bottom
    slideUp: `
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `,
    // Scale in
    scaleIn: `
      @keyframes scaleIn {
        from {
          opacity: 0;
          transform: scale(0.9);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }
    `,
  },
  
  // Transitions
  transitions: {
    fast: '0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    base: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    spring: '0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    bounce: '0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },
  
  // Typography
  typography: {
    fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
    fontFamilyDisplay: "'Clash Display', 'Inter', sans-serif",
    h1: { fontSize: '2.5rem', fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.01em' },
    h4: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.01em' },
    h5: { fontSize: '1.25rem', fontWeight: 600 },
    h6: { fontSize: '1rem', fontWeight: 600 },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
  },
  
  // Spacing
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem',
    xxxl: '4rem',
  },
  
  // Breakpoints
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1440px',
    wide: '1920px',
  },
};

// Media query helpers
export const liquidDevice = {
  mobile: `@media (max-width: ${liquidTheme.breakpoints.mobile})`,
  tablet: `@media (min-width: calc(${liquidTheme.breakpoints.mobile} + 1px)) and (max-width: ${liquidTheme.breakpoints.tablet})`,
  desktop: `@media (min-width: calc(${liquidTheme.breakpoints.tablet} + 1px))`,
  wide: `@media (min-width: ${liquidTheme.breakpoints.wide})`,
};

// Theme-aware helper functions
export const getGlassBackground = (theme) => {
  const isLight = theme?.name === 'light';
  const baseGlass = isLight ? liquidTheme.light.glass.background : liquidTheme.dark.glass.background;
  
  // If theme has colorTheme, add a subtle tint from primary color
  if (theme?.colorTheme?.primary?.main) {
    const primaryColor = theme.colorTheme.primary.main;
    // Extract RGB values from hex color
    const hex = primaryColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const opacity = isLight ? '0.08' : '0.05';
    const tintedGlass = `rgba(${r}, ${g}, ${b}, ${opacity})`;
    
    // Blend with base glass using a subtle gradient
    return `linear-gradient(135deg, ${tintedGlass} 0%, ${baseGlass} 100%)`;
  }
  
  return baseGlass;
};

export const getGlassBackgroundHover = (theme) => {
  const isLight = theme?.name === 'light';
  const baseGlass = isLight ? liquidTheme.light.glass.backgroundHover : liquidTheme.dark.glass.backgroundHover;
  
  // If theme has colorTheme, add a more pronounced tint from primary color on hover
  if (theme?.colorTheme?.primary?.main) {
    const primaryColor = theme.colorTheme.primary.main;
    const hex = primaryColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const opacity = isLight ? '0.12' : '0.08';
    const tintedGlass = `rgba(${r}, ${g}, ${b}, ${opacity})`;
    
    return `linear-gradient(135deg, ${tintedGlass} 0%, ${baseGlass} 100%)`;
  }
  
  return baseGlass;
};

export const getGlassBorder = (theme) =>
  theme?.name === 'light' ? liquidTheme.light.glass.border : liquidTheme.dark.glass.border;

export const getGlassShadow = (theme) =>
  theme?.name === 'light' ? liquidTheme.light.glass.shadow : liquidTheme.dark.glass.shadow;

export const getTextPrimary = (theme) =>
  theme?.name === 'light' ? liquidTheme.light.text.primary : liquidTheme.dark.text.primary;

export const getTextSecondary = (theme) =>
  theme?.name === 'light' ? liquidTheme.light.text.secondary : liquidTheme.dark.text.secondary;

export const getTextMuted = (theme) =>
  theme?.name === 'light' ? liquidTheme.light.text.muted : liquidTheme.dark.text.muted;

export const getBackgroundGradient = (theme) =>
  theme?.name === 'light' ? liquidTheme.light.background.gradient : liquidTheme.dark.background.gradient;

export const getBackgroundMain = (theme) =>
  theme?.name === 'light' ? liquidTheme.light.background.main : liquidTheme.dark.background.main;

// Helper functions to get colors from selected color theme
export const getPrimaryColor = (theme) => {
  return theme?.colorTheme?.primary?.main || liquidTheme.colors.primary.main;
};

export const getPrimaryGradient = (theme) => {
  return theme?.colorTheme?.primary?.gradient || liquidTheme.colors.primary.gradient;
};

export const getPrimaryGlow = (theme, opacity = 0.4) => {
  const glowColor = theme?.colorTheme?.primary?.glow || liquidTheme.colors.primary.glow;
  // Extract rgba values and adjust opacity
  if (glowColor.includes('rgba')) {
    return glowColor.replace(/[\d.]+(?=\))/, opacity);
  }
  return glowColor;
};

export const getSecondaryColor = (theme) => {
  return theme?.colorTheme?.secondary?.main || liquidTheme.colors.secondary.main;
};

export const getSecondaryGradient = (theme) => {
  return theme?.colorTheme?.secondary?.gradient || liquidTheme.colors.secondary.gradient;
};

export const getSecondaryGlow = (theme, opacity = 0.4) => {
  const glowColor = theme?.colorTheme?.secondary?.glow || liquidTheme.colors.secondary.glow;
  // Extract rgba values and adjust opacity
  if (glowColor.includes('rgba')) {
    return glowColor.replace(/[\d.]+(?=\))/, opacity);
  }
  return glowColor;
};

export const getSuccessColor = (theme) => {
  return theme?.colorTheme?.success?.main || liquidTheme.colors.success.main;
};

export const getSuccessGradient = (theme) => {
  return theme?.colorTheme?.success?.gradient || liquidTheme.colors.success.gradient;
};

export const getWarningColor = (theme) => {
  return theme?.colorTheme?.warning?.main || liquidTheme.colors.warning.main;
};

export const getWarningGradient = (theme) => {
  return theme?.colorTheme?.warning?.gradient || liquidTheme.colors.warning.gradient;
};

export const getDangerColor = (theme) => {
  return theme?.colorTheme?.danger?.main || liquidTheme.colors.danger.main;
};

export const getDangerGradient = (theme) => {
  return theme?.colorTheme?.danger?.gradient || liquidTheme.colors.danger.gradient;
};

export const getDangerGlow = (theme, opacity = 0.4) => {
  const glowColor = theme?.colorTheme?.danger?.glow || liquidTheme.colors.danger.glow;
  // Extract rgba values and adjust opacity
  if (glowColor.includes('rgba')) {
    return glowColor.replace(/[\d.]+(?=\))/, opacity);
  }
  return glowColor;
};

export const getSuccessGlow = (theme, opacity = 0.4) => {
  const glowColor = theme?.colorTheme?.success?.glow || liquidTheme.colors.success.glow;
  // Extract rgba values and adjust opacity
  if (glowColor.includes('rgba')) {
    return glowColor.replace(/[\d.]+(?=\))/, opacity);
  }
  return glowColor;
};

export const getWarningGlow = (theme, opacity = 0.4) => {
  const glowColor = theme?.colorTheme?.warning?.glow || liquidTheme.colors.warning.glow;
  // Extract rgba values and adjust opacity
  if (glowColor.includes('rgba')) {
    return glowColor.replace(/[\d.]+(?=\))/, opacity);
  }
  return glowColor;
};

export const getEmergencyColor = (theme) => {
  return theme?.colorTheme?.emergency?.main || liquidTheme.colors.danger.main;
};

export const getEmergencyGradient = (theme) => {
  return theme?.colorTheme?.emergency?.gradient || liquidTheme.colors.danger.gradient;
};

export default liquidTheme;

